import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTimezoneOptions } from '../../helpers/TimeZones';
import { getDefaultTimeZoneListData } from '../../helpers/Dates';
import { getTimezoneCountryCode } from '../../helpers/TimezoneCountry';
import 'flag-icons/css/flag-icons.min.css';
import './TimezoneSelector.scss';

/**
 * Compact timezone selector with flag icon.
 *
 * Props:
 *   value     – timezone object { value, label, offset, abbrev, index }
 *   onChange  – (tzObject) => void
 *   className – optional extra CSS class
 */
const TimezoneSelector = ({ value, onChange, className = '', align = 'right' }) => {
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    // Load timezone options and set browser-default on mount
    useEffect(() => {
        const opts = getTimezoneOptions();
        setOptions(opts);
        if (!value) {
            const defTz = getDefaultTimeZoneListData(opts);
            if (defTz) onChange(defTz);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen]);

    const filteredOptions = options.filter(tz => {
        const q = search.toLowerCase();
        return (
            tz.label.toLowerCase().includes(q) ||
            (tz.abbrev || '').toLowerCase().includes(q) ||
            tz.value.toLowerCase().includes(q)
        );
    });

    const handleSelect = useCallback((tz) => {
        onChange(tz);
        setIsOpen(false);
        setSearch('');
    }, [onChange]);

    const flagCode = value ? getTimezoneCountryCode(value.value) : null;
    const abbrev = value?.abbrev || 'UTC';

    return (
        <div className={`tz-selector${className ? ` ${className}` : ''}`} ref={containerRef}>
            <button
                className="tz-selector-trigger"
                onClick={() => setIsOpen(v => !v)}
                title={value?.label || 'Select timezone'}
                type="button"
            >
                {flagCode
                    ? <span className={`fi fi-${flagCode} tz-flag`} aria-hidden="true" />
                    : <span className="tz-globe" aria-hidden="true">🌐</span>
                }
                <span className="tz-abbrev">{abbrev}</span>
                <span className="tz-caret" aria-hidden="true" />
            </button>

            {isOpen && (
                <div className={`tz-dropdown${align === 'left' ? ' tz-dropdown--left' : ''}`} role="listbox" aria-label="Select timezone">
                    <div className="tz-search-wrap">
                        <input
                            ref={searchRef}
                            className="tz-search"
                            placeholder="Search timezone…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            aria-label="Search timezones"
                        />
                    </div>
                    <ul className="tz-list">
                        {filteredOptions.map(tz => {
                            const code = getTimezoneCountryCode(tz.value);
                            const isSelected = tz.value === value?.value;
                            return (
                                <li
                                    key={tz.value}
                                    className={`tz-option${isSelected ? ' tz-option--selected' : ''}`}
                                    onClick={() => handleSelect(tz)}
                                    role="option"
                                    aria-selected={isSelected}
                                >
                                    {code
                                        ? <span className={`fi fi-${code} tz-flag`} aria-hidden="true" />
                                        : <span className="tz-globe tz-globe--sm" aria-hidden="true">🌐</span>
                                    }
                                    <span className="tz-option-label">{tz.label}</span>
                                </li>
                            );
                        })}
                        {filteredOptions.length === 0 && (
                            <li className="tz-option tz-option--empty">No timezones found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TimezoneSelector;
